import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import type { PrimeXQuoteHandlers, PrimeXQuoteState } from '../types';
import { raterXRecalculatePayloadBuilder } from '../utils/rater-x/helpers';
import { recalculateRaterX } from '../api';
import { isRecordIntact } from '../../prime-100/utils';
import { formatCoveragesForState } from '../transformers/coverages';
import type {
  RaterXPreset,
  RecalculateRaterXPayload,
} from '../api/types/rater-x-preset';
import type { EditQuoteState } from '../views/edit-quote/types';
import { formatRatingOptions } from '../transformers/uw/ratingOptions';
import type { CustomizeQuoteState } from '../views/customize-quote/types';
import { formatComponentCoveragesForState } from '../transformers/componentCoverages';
import { formatComponentGlobals } from '../transformers/componentGlobals';
import { formatComponentModifiersForState } from '../transformers/uw/componentRateModifiers';

type UseRaterXRecalculateOptions = {
  isOneShotFlow?: boolean;
};

type RecalculateState = PrimeXQuoteState &
  Partial<EditQuoteState> &
  Partial<CustomizeQuoteState>;

/**
 * What Customize sends to re-rate its quote: the quote's state as it stands, strict until a global changes. Exported so
 * the broker experience re-rates a product's option with the same request.
 */
export function recalculatePayload(quoteState: RecalculateState) {
  return raterXRecalculatePayloadBuilder({
    effectiveDate: quoteState.effectiveDate,
    endDate: quoteState.endDate,
    productType: quoteState.productType,
    fieldValues: quoteState.fieldValues,
    account: quoteState.account,
    risk: quoteState.risk,
    policyValue: quoteState.global?.value,
    coverages: quoteState.coverageState,
    strictMode: !quoteState.globalChanged,
    ratingOptions: quoteState.ratingOptions,
    productVersion:
      quoteState.quote?.quote.raterResponse?.raterResponseFlags.productVersion,
    commissionRate: quoteState.commissionRate,
    ...(quoteState.quoteCommissionRate != null && {
      quoteCommissionRate: quoteState.quoteCommissionRate,
    }),
    isAgencyBilled: quoteState.isAgencyBilled,
    agencyName: quoteState.agencyName,
    marketType: quoteState.marketType,
    categoryCustomizations: quoteState.categoryCustomizations,
    agencyId: quoteState.agencyId,
    accountId: quoteState.account.id,
    isRenewal: quoteState.isRenewal,
    componentGlobals: quoteState.componentGlobals,
    quoteComponentsRaterResponse:
      quoteState.quote?.quote.raterResponse?.components,
    brokerFee: quoteState.brokerFee,
    carrierPrefix: quoteState.carrierPrefix,
    treatyId: quoteState.treatyId,
  });
}

/**
 * What a re-rating writes back to the quote's state: its coverages, rating options, globals, carrier and treaty, and the
 * dates the rater moved it to. Exported so the broker experience reads a re-rating the same way.
 */
export function recalculatedState(
  response: RaterXPreset
): Parameters<PrimeXQuoteHandlers['setRaterXRecalculated']>[0] {
  const effectiveDate = response.effectiveDate?.date;
  const endDate = response.endDate?.date;
  return {
    componentGlobals: formatComponentGlobals(response.components),
    coverages: {
      ...formatComponentCoveragesForState(response.components),
      ...formatCoveragesForState(response.coverageCategories),
    },
    ratingOptions: {
      ...formatComponentModifiersForState(response.components),
      ...formatRatingOptions(response.rateModifierCategories),
    },
    global: response.global,
    carrierPrefix: response.carrierPrefix,
    treatyId: response.treatyId,
    ...(endDate && { endDate }),
    ...(effectiveDate && { effectiveDate }),
  };
}

const useRaterXRecalculate = <T extends RecalculateState>(
  quoteState: T,
  setRaterXRecalculated: PrimeXQuoteHandlers['setRaterXRecalculated'],
  options?: UseRaterXRecalculateOptions
) => {
  const isOneShotFlow = options?.isOneShotFlow ?? false;
  const payload = recalculatePayload(quoteState);

  return useQuery({
    queryKey: ['rater-x-recalculate'],
    queryFn: () =>
      recalculateRaterX(quoteState.quote?.quote.id, quoteState.submission?.id)(
        quoteState.productType,
        {
          data: payload as RecalculateRaterXPayload,
        }
      ).then((response) => {
        const isDeclinedButSaveable =
          isOneShotFlow &&
          response.data.declines === true &&
          response.data.valid !== false;

        if (isDeclinedButSaveable) {
          // Do not update any state - PrimeXEditQuoteView sync effect will set declination info in one-shot context
          return response.data;
        }

        setRaterXRecalculated(recalculatedState(response.data));
        return response.data;
      }),
    enabled:
      Boolean(quoteState.enableRaterXRecalculate) &&
      isRecordIntact(payload) &&
      !_.isEmpty(payload),
    placeholderData: quoteState.raterX,
    staleTime: 0,
    cacheTime: 0,
  });
};

export default useRaterXRecalculate;
